import braintree from 'braintree-web';
import dynamic from 'next/dynamic';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import type { SubscriptionPlan } from 'src/models/subscription-plan/subscription-plan';
import { AnalyticsPaymentMethod } from 'src/utils/payment/payment-analytics';

import { useAnalytics } from '@features/Analytics';
import * as payments from '@features/Paywall/api/payments';

import {
  PaymentCompletionStatus,
  usePaymentCompletionStatus,
} from './payment-completion-status';

export const BraintreeModal = dynamic(
  () => import('../../components/main/payment/braintree-modal/braintree-modal'),
  { ssr: false },
);

export type PaymentSystem = 'BRAINTREE';

const BRAINTREE_APPLE_PAY_STORE_NAME = 'Youth';

const usePaymentSystem = ({
  subscriptionPlan,
  paymentSystem,
  setIsLoading,
  onLoadEnd,
  braintreeOptions = {},
}: {
  subscriptionPlan: SubscriptionPlan;
  paymentSystem?: PaymentSystem;
  setIsLoading: (value: boolean) => void;
  onLoadEnd?: () => void;
  braintreeOptions?: { useApplePay?: boolean };
}): {
  contentNode?: ReactNode;
  openCheckout: () => Promise<void>;
  openCheckoutWithoutGesture: () => Promise<void>;
} => {
  const { trackGoogleEvent } = useAnalytics();
  const { authStore, paymentsStore } = useStores();
  const { setWasPaymentClosed } = paymentsStore;
  const { user, auth_token } = authStore;

  const { setPaymentCompletionStatus } = usePaymentCompletionStatus();

  // #region Braintree specific

  const getBraintreeClientToken = useCallback(
    () =>
      payments.generateBraintreeClientToken({
        customerId: user!.serverId.toString(),
        customerEmail: user!.email,
        merchantAccountId: subscriptionPlan.merchantAccountId,
      }),
    [subscriptionPlan.merchantAccountId, user],
  );

  // The braintree client for Apple Pay must be loaded in advance,
  // because Safari allows to initiate an Apple Pay session only
  // inside a gesture handler
  const braintreeApplePayClient = useRef<braintree.Client | undefined>();
  useEffect(() => {
    if (paymentSystem === 'BRAINTREE' && braintreeOptions.useApplePay) {
      setIsLoading(true);
      (async () => {
        try {
          const clientToken = await getBraintreeClientToken();
          braintreeApplePayClient.current = await braintree.client.create({
            authorization: clientToken,
          });
        } finally {
          setIsLoading(false);
          onLoadEnd?.();
        }
      })();
    }
  }, [
    paymentSystem,
    setIsLoading,
    braintreeOptions.useApplePay,
    getBraintreeClientToken,
    onLoadEnd,
  ]);

  const [braintreeShowModal, setBraintreeShowModal] = useState(false);
  const [braintreeClientToken, setBraintreeClientToken] = useState<
    string | undefined
  >();
  const braintreeHandleModalClose = () => {
    setBraintreeShowModal(false);
    setWasPaymentClosed(true);
  };

  const handleBraintreeCheckoutComplete = useCallback(
    async (paymentMethodNonce: string, paymentMethodVaulted?: boolean) => {
      try {
        await payments.createBraintreeSubscription({
          customerId: user!.serverId.toString(),
          customerToken: auth_token!,
          merchantAccountId: subscriptionPlan.merchantAccountId,
          planId: subscriptionPlan.braintreePlanId!,
          paymentMethodNonce,
          paymentMethodVaulted,
        });
        // This prevents flashing of the alert with a loading message,
        // because the subscription is expected to activate right away
        // in most cases
        await authStore.fetchUser({ token: auth_token });
        setPaymentCompletionStatus(PaymentCompletionStatus.SUCCESS);
      } catch (error) {
        setPaymentCompletionStatus(PaymentCompletionStatus.FAILURE);
        console.error(error);
      } finally {
        setBraintreeShowModal(false);
      }
    },
    [
      user,
      auth_token,
      subscriptionPlan.merchantAccountId,
      subscriptionPlan.braintreePlanId,
      authStore,
      setPaymentCompletionStatus,
    ],
  );

  // endregion

  const openCheckout = useCallback(async () => {
    if (paymentSystem == null) {
      return;
    }

    if (!user) {
      throw new Error('A user is required');
    }

    setIsLoading(true);

    switch (paymentSystem) {
      case 'BRAINTREE': {
        try {
          if (braintreeOptions.useApplePay) {
            const client = braintreeApplePayClient.current!;
            const applePay = await braintree.applePay.create({ client });
            const paymentRequest = applePay.createPaymentRequest({
              total: {
                label: subscriptionPlan.name,
                amount: subscriptionPlan.immediatePrice.toFixed(2),
              },
            });

            // @ts-ignore (an issue with library typings)
            const session = ApplePaySession.supportsVersion(14)
              ? // @ts-ignore (an issue with library typings)
                new ApplePaySession(14, paymentRequest)
              : // @ts-ignore (an issue with library typings)
                new ApplePaySession(3, paymentRequest);

            session.onvalidatemerchant = async (event) => {
              const merchantSession = await applePay.performValidation({
                validationURL: event.validationURL,
                displayName: BRAINTREE_APPLE_PAY_STORE_NAME,
              });
              session.completeMerchantValidation(merchantSession);
            };

            session.onpaymentauthorized = async (event) => {
              let nonce: string;
              try {
                ({ nonce } = await applePay.tokenize({
                  token: event.payment.token,
                }));
              } catch (error) {
                session.completePayment(ApplePaySession.STATUS_FAILURE);
                console.error(error);
                return;
              }
              try {
                await handleBraintreeCheckoutComplete(nonce, false);
              } finally {
                session.completePayment(ApplePaySession.STATUS_SUCCESS);
              }
            };

            session.begin();
            // eslint-disable-next-line  unicorn/prefer-add-event-listener
            session.oncancel = () => {
              trackGoogleEvent({
                eventName: 'add_payment_info_skipped',
                options: {
                  payment_system: 'braintree',
                  payment_method: AnalyticsPaymentMethod.APPLE_PAY,
                },
              });
            };
          } else {
            const clientToken = await getBraintreeClientToken();
            setBraintreeClientToken(clientToken);
            setBraintreeShowModal(true);
          }
        } finally {
          setIsLoading(false);
        }
        break;
      }
    }
  }, [
    paymentSystem,
    user,
    setIsLoading,
    braintreeOptions.useApplePay,
    subscriptionPlan.name,
    subscriptionPlan.immediatePrice,
    handleBraintreeCheckoutComplete,
    trackGoogleEvent,
    getBraintreeClientToken,
  ]);

  const openCheckoutWithoutGesture = useCallback(async () => {
    switch (paymentSystem) {
      case 'BRAINTREE': {
        if (braintreeOptions.useApplePay) {
          return;
        }
        break;
      }
    }
    await openCheckout();
  }, [braintreeOptions.useApplePay, openCheckout, paymentSystem]);

  // A workaround for Paddle's `<meta name="viewport" />`
  // causing zoom issues in iOS Safari (Paddle only)

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          document
            .querySelector('[id="__mobileViewportControl_hook__"]')
            ?.remove();
        }
      }
    });
    observer.observe(document.querySelector('head')!, {
      childList: true,
    });
    return () => {
      observer.disconnect();
    };
  }, []);

  // Rendering necessary elements

  let contentNode: ReactNode | undefined;
  switch (paymentSystem) {
    case 'BRAINTREE': {
      contentNode = (
        <BraintreeModal
          subscriptionPlan={subscriptionPlan}
          show={braintreeShowModal}
          onHide={braintreeHandleModalClose}
          clientToken={braintreeClientToken}
          onCheckoutComplete={handleBraintreeCheckoutComplete}
        />
      );
      break;
    }
  }

  return { contentNode, openCheckout, openCheckoutWithoutGesture };
};

export default usePaymentSystem;
